import { ClientSelectOption } from '../../components/select/SelectClient'
import {
  DISABLED_REASON,
  DisabledReason,
} from '../../pages/referClient/steps/2_SelectServiceOrAdviser/components/SelectServiceFirm/constants'
import { ClientDetailsInput } from '../../pages/referClient/steps/4_ClientDetails/constants'
import { REFERRAL_METHOD, ReferralMethod } from '../../redux/slices/webReferral'
import {
  NormalReferralTypeOption,
  ReferralIndividual,
  ReferralTypeOption,
} from '../../types/misc'
import {
  CreateClientForReferralRequest,
  UpdateClientForReferralRequest,
} from '../../types/requests/clients'
import { CompanyTypeItem } from '../../types/responses/common-data'
import {
  ReferralFirmCollectionItem,
  ReferralFirmIndividualsCollectionItem,
} from '../../types/responses/companies'
import {
  DATE_FORMATS,
  FCA_SERVICE_TYPE_NAMES,
  PANEL_STATUS,
  REFERRAL_TYPE_OPTION,
  SERVICE_AREA_ID,
} from '../constants'
import {
  convertDate,
  isValidDateFormat,
  normalizeDateOfBirth,
} from '../helpers/helperFunctions'
import clientService from './clientService'

class WebReferralService {
  isNormalReferralType(
    referralTypeOption?: ReferralTypeOption,
  ): referralTypeOption is NormalReferralTypeOption {
    return (
      !!referralTypeOption &&
      referralTypeOption !== REFERRAL_TYPE_OPTION.bookCallForClient
    )
  }

  isBookCallForClientReferralType(
    referralTypeOption?: ReferralTypeOption,
  ): boolean {
    return (
      !!referralTypeOption &&
      referralTypeOption === REFERRAL_TYPE_OPTION.bookCallForClient
    )
  }

  isValidReferralTarget(value?: unknown): value is ReferralMethod {
    return (
      typeof value === 'string' && Object.keys(REFERRAL_METHOD).includes(value)
    )
  }

  getMostRestrictedServiceType(
    selectedServices: ReferralFirmCollectionItem['services'],
  ): string {
    if (selectedServices.length === 0) {
      return FCA_SERVICE_TYPE_NAMES.no
    }

    const servicesOrderedByRestrictionLevel = selectedServices.sort(
      (serviceA, serviceB) => {
        const serviceARestrictionLevel =
          serviceA.serviceType?.restrictionLevel || 0
        const serviceBRestrictionLevel =
          serviceB.serviceType?.restrictionLevel || 0

        return serviceBRestrictionLevel - serviceARestrictionLevel
      },
    )

    const mostRestrictedService = servicesOrderedByRestrictionLevel[0]

    return mostRestrictedService?.serviceType?.name || FCA_SERVICE_TYPE_NAMES.no
  }

  makeReferralFirmIndividual(
    individual: ReferralFirmIndividualsCollectionItem,
  ): ReferralIndividual {
    return {
      companyUserId: individual.id,
      userId: individual.user.id,
      fullName: individual.user.fullName,
      email: individual.user.email,
      company: individual.company,
    }
  }

  clearCommissionQuestions(
    clientDetails: ClientDetailsInput,
  ): ClientDetailsInput {
    return {
      ...clientDetails,
      isExpectingCommission: undefined,
      knowsCommissionAmount: undefined,
      expectedCommissionAmount: undefined,
    }
  }

  getFirmDisabledReason(options: {
    referralFirm: ReferralFirmCollectionItem
    companyTypes: CompanyTypeItem[]
    selectedServiceAreaIds: number[]
  }): DisabledReason | undefined {
    const { referralFirm, companyTypes, selectedServiceAreaIds } = options

    const matchingServices = referralFirm.services.filter((service) =>
      selectedServiceAreaIds.includes(service.serviceArea.id),
    )

    const firmHasAllSelectedServices =
      matchingServices.length === selectedServiceAreaIds.length

    const mostRestrictedServiceType =
      webReferralService.getMostRestrictedServiceType(matchingServices)

    const isApproved =
      referralFirm.panelWithCurrentCompany.status === PANEL_STATUS.approved

    const companyType = companyTypes.find(
      (companyType) => companyType['@id'] === referralFirm.companyType,
    )

    const isFcaFirm = companyType?.fca

    const nonStandaloneServicesHaveFinancialPlanSelected =
      !matchingServices.some((service) => !service.isStandaloneService) ||
      selectedServiceAreaIds.includes(SERVICE_AREA_ID.creationOfAFinancialPlan)

    if (!firmHasAllSelectedServices && !isApproved) {
      return DISABLED_REASON.missingServiceAndUnapproved
    }

    if (!firmHasAllSelectedServices) {
      return DISABLED_REASON.missingService
    }

    if (!isApproved) {
      return DISABLED_REASON.unapproved
    }

    if (!nonStandaloneServicesHaveFinancialPlanSelected) {
      return DISABLED_REASON.nonStandaloneServicesHaveFinancialPlanSelected
    }

    if (isFcaFirm && mostRestrictedServiceType === FCA_SERVICE_TYPE_NAMES.no) {
      return DISABLED_REASON.fcaFirmRestrictedService
    }

    return
  }

  buildCreateClientRequest(
    input: ClientDetailsInput,
  ): CreateClientForReferralRequest {
    const [firstName, lastName] = clientService.getFirstAndLastName(
      input.fullName || '',
    )

    const dateOfBirth = input.dateOfBirth
      ? normalizeDateOfBirth(input.dateOfBirth)
      : undefined

    const request: CreateClientForReferralRequest = {
      firstName: firstName || '',
      lastName: lastName || '',
      email: input.email,
      dateOfBirth,
      telephone: input.telephone || '',
      clientId: input.clientId || '',
      addressLine1: input.addressLine1 || '',
      city: input.city || '',
    }

    if (input.postcode) {
      request.postcode = input.postcode
    }

    return request
  }

  getPatchedFields(
    client: ClientSelectOption | null,
    formValues?: ClientDetailsInput,
  ): Array<keyof UpdateClientForReferralRequest> {
    const fields: Array<keyof UpdateClientForReferralRequest> = []

    // If we don't have a client, then no fields have been patched
    if (!client || !formValues) {
      return fields
    }

    if (!client.telephone && !!formValues.telephone) {
      fields.push('telephone')
    }

    if (!client.dateOfBirth && !!formValues.dateOfBirth) {
      fields.push('dateOfBirth')
    }

    if (!client.clientId && !!formValues.clientId) {
      fields.push('clientId')
    }

    if (!client.addressLine1 && !!formValues.addressLine1) {
      fields.push('addressLine1')
    }

    if (!client.city && !!formValues.city) {
      fields.push('city')
    }

    if (!client.postcode && !!formValues.postcode) {
      fields.push('postcode')
    }

    return fields
  }

  buildInitialClientDetailsValues(
    previousInput?: ClientDetailsInput,
  ): ClientDetailsInput {
    const expectedCommissionAmount =
      typeof previousInput?.expectedCommissionAmount === 'number'
        ? previousInput.expectedCommissionAmount
        : undefined

    return {
      fullName: previousInput?.fullName || '',
      email: previousInput?.email || '',
      telephone: previousInput?.telephone || '',
      dateOfBirth: webReferralService.getFormattedDateOfBirth(
        previousInput?.dateOfBirth,
      ),
      clientId: previousInput?.clientId || '',
      addressLine1: previousInput?.addressLine1 || '',
      city: previousInput?.city || '',
      postcode: previousInput?.postcode || '',
      isExpectingCommission: previousInput?.isExpectingCommission,
      knowsCommissionAmount: previousInput?.knowsCommissionAmount,
      expectedCommissionAmount,
      circumstances: previousInput?.circumstances || '',
      reason: previousInput?.reason || '',
    }
  }

  // Get a date of birth in the DD/MM/YYYY format
  getFormattedDateOfBirth(dateOfBirth?: string | null): string | undefined {
    // Extract just the date part from the datetime string
    const date = (dateOfBirth || '').split('T')[0] || ''

    // No date, so return undefined
    if (!date) {
      return
    }

    // Date of birth format already valid, so return it
    if (isValidDateFormat(date, DATE_FORMATS.DAY_MONTH_YEAR)) {
      return date
    }

    // Date of birth format is YYYY-MM-DD, so convert it to DD/MM/YYYY
    if (isValidDateFormat(date, DATE_FORMATS.YEAR_MONTH_DAY)) {
      return convertDate(
        date,
        DATE_FORMATS.YEAR_MONTH_DAY,
        DATE_FORMATS.DAY_MONTH_YEAR,
      )
    }

    return undefined
  }
}

const webReferralService = new WebReferralService()

export default webReferralService
